import dayjs from "dayjs";
import { FilingReportItem } from "../../../api";
import { FILING_REPORT_STATUS_OPTIONS } from "./filingReportStatusOptions";
import {
  EMPTY_FILTERS,
  FILTER_FIELD_LABELS,
  STATUS_FILTER_NONE,
  type FilterFieldKey,
  type FilingFiltersState,
} from "./types";

export function countActiveFilters(filters: FilingFiltersState): number {
  let count = 0;
  (Object.keys(filters) as (keyof FilingFiltersState)[]).forEach((key) => {
    const value = filters[key];
    if (
      key === "createdFrom" ||
      key === "createdTo" ||
      key === "dueFrom" ||
      key === "dueTo"
    ) {
      if (value != null) count += 1;
      return;
    }
    if (typeof value === "string" && value.trim()) count += 1;
  });
  return count;
}

export function hasActiveFilters(filters: FilingFiltersState): boolean {
  return countActiveFilters(filters) > 0;
}

export function clearFilterField(
  filters: FilingFiltersState,
  key: keyof FilingFiltersState
): FilingFiltersState {
  if (
    key === "createdFrom" ||
    key === "createdTo" ||
    key === "dueFrom" ||
    key === "dueTo"
  ) {
    return { ...filters, [key]: null };
  }
  return { ...filters, [key]: "" };
}

export function resetFilters(): FilingFiltersState {
  return { ...EMPTY_FILTERS };
}

export function uniqueStringOptions(
  rows: FilingReportItem[],
  field: "caseName" | "taskName" | "practiceArea"
): string[] {
  const values = new Set<string>();
  rows.forEach((row) => {
    const value = (row[field] ?? "").trim();
    if (value) values.add(value);
  });
  return [...values].sort((a, b) => a.localeCompare(b));
}

export function statusFilterOptions(): string[] {
  return [STATUS_FILTER_NONE, ...FILING_REPORT_STATUS_OPTIONS];
}

export function applyFilingFilters(
  rows: FilingReportItem[],
  filters: FilingFiltersState
): FilingReportItem[] {
  let data = rows;

  if (filters.caseName) {
    data = data.filter(
      (row) =>
        (row.caseName ?? "").trim().toLowerCase() === filters.caseName.trim().toLowerCase()
    );
  }

  if (filters.taskName) {
    data = data.filter((row) => row.taskName === filters.taskName);
  }

  if (filters.practiceArea) {
    data = data.filter((row) => row.practiceArea === filters.practiceArea);
  }

  if (filters.status === STATUS_FILTER_NONE) {
    data = data.filter((row) => !row.status);
  } else if (filters.status) {
    data = data.filter((row) => row.status === filters.status);
  }

  if (filters.overdue === "Yes") {
    data = data.filter(
      (row) => !!row.dueDate && dayjs(row.dueDate).isBefore(dayjs(), "day")
    );
  } else if (filters.overdue === "No") {
    data = data.filter(
      (row) => !row.dueDate || !dayjs(row.dueDate).isBefore(dayjs(), "day")
    );
  }

  if (filters.createdFrom) {
    data = data.filter(
      (row) =>
        !!row.createdAt &&
        !dayjs(row.createdAt).isBefore(filters.createdFrom!, "day")
    );
  }

  if (filters.createdTo) {
    data = data.filter(
      (row) =>
        !!row.createdAt && !dayjs(row.createdAt).isAfter(filters.createdTo!, "day")
    );
  }

  if (filters.dueFrom) {
    data = data.filter(
      (row) => !!row.dueDate && !dayjs(row.dueDate).isBefore(filters.dueFrom!, "day")
    );
  }

  if (filters.dueTo) {
    data = data.filter(
      (row) => !!row.dueDate && !dayjs(row.dueDate).isAfter(filters.dueTo!, "day")
    );
  }

  if (filters.searchTerm.trim()) {
    const lower = filters.searchTerm.trim().toLowerCase();
    data = data.filter(
      (row) =>
        row.caseName?.toLowerCase().includes(lower) ||
        row.practiceArea?.toLowerCase().includes(lower) ||
        row.caseTeam?.toLowerCase().includes(lower) ||
        row.taskName?.toLowerCase().includes(lower) ||
        row.status?.toLowerCase().includes(lower) ||
        row.note?.toLowerCase().includes(lower) ||
        row.statusHistory?.some((entry) => entry.status?.toLowerCase().includes(lower))
    );
  }

  return data;
}

export function buildActiveChips(filters: FilingFiltersState) {
  const chips: { key: keyof FilingFiltersState; label: string }[] = [];
  (
    ["caseName", "taskName", "practiceArea", "status", "overdue", "searchTerm"] as FilterFieldKey[]
  ).forEach((field) => {
    if (!filters[field]) return;
    const value =
      field === "status" && filters.status === STATUS_FILTER_NONE
        ? "No status"
        : filters[field];
    chips.push({
      key: field,
      label: `${FILTER_FIELD_LABELS[field]}: ${value}`,
    });
  });
  if (filters.createdFrom) {
    chips.push({
      key: "createdFrom",
      label: `Created from: ${filters.createdFrom.format("MM/DD/YYYY")}`,
    });
  }
  if (filters.createdTo) {
    chips.push({
      key: "createdTo",
      label: `Created to: ${filters.createdTo.format("MM/DD/YYYY")}`,
    });
  }
  if (filters.dueFrom) {
    chips.push({
      key: "dueFrom",
      label: `Due from: ${filters.dueFrom.format("MM/DD/YYYY")}`,
    });
  }
  if (filters.dueTo) {
    chips.push({
      key: "dueTo",
      label: `Due to: ${filters.dueTo.format("MM/DD/YYYY")}`,
    });
  }
  return chips;
}
